'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Script from 'next/script'
import {
  appLocalStorage,
  cartIdSelector,
  cartTotalCountSelector,
  KEY_CART_ID,
  resolveCatchMessage,
  TCatchMessage,
  useCart,
  useLazyGetRouteConfigQuery,
  useLoadingContext,
  useSetRouteIsInsuredMutation,
  useToastContext,
} from '@segway/core'
import { RouteConfigQuery } from '@segway/core/src/graphql/generated/graphql'
import { useAppSelector } from '@segway/core/src/store/hooks'
import { useToggle, useUpdateEffect } from 'ahooks'

import { usePathname } from '@/i18n/navigation'

import './routeWidget.css'

type TRouteWidgetProps = {
  enabled: boolean
  isChecked: boolean
}

/**
 * 保险 widget
 */
const RouteWidget = ({ enabled, isChecked }: TRouteWidgetProps) => {
  const loading = useLoadingContext()
  const toast = useToastContext()

  const pathname = usePathname()
  const cartId = useAppSelector(cartIdSelector)
  const cartTotalCount = useAppSelector(cartTotalCountSelector)
  const [toggleRefreshCartState, { toggle: toggleRefreshCart }] = useToggle()

  const { refreshCart } = useCart()

  const [getRouteConfigQuery] = useLazyGetRouteConfigQuery()
  const [setRouteIsInsuredMutation] = useSetRouteIsInsuredMutation()

  const [loaded, setLoaded] = useState(false)
  const isCheckedRef = useRef(isChecked)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleInsuranceToggle = useCallback(
    async (data: unknown) => {
      const newData = data as { from: number; to: number }
      const localCartId = appLocalStorage.getItemSync(KEY_CART_ID) as string

      try {
        loading.show()
        isCheckedRef.current = !!newData.to
        const response = await setRouteIsInsuredMutation({
          cartId: localCartId,
          inSured: !!newData.to,
        }).unwrap()

        if (response.setIsInsured?.success) {
          toggleRefreshCart()
        }
      } catch (error) {
        toast.show({
          icon: 'fail',
          content: resolveCatchMessage(error as TCatchMessage),
        })
      } finally {
        loading.hide()
      }
    },
    [loading, toast, setRouteIsInsuredMutation, toggleRefreshCart],
  )

  const renderRoute = useCallback(
    (data: NonNullable<RouteConfigQuery['routeConfig']>) => {
      if (!data) {
        return
      }

      if (window?.Route?.Protection?.render) {
        window?.Route?.Protection.render({
          storeDomain: data.store_domain,
          subtotal: data.subtotal,
          currency: data.currency,
          environment: window.Route.Environment.Production,
          status: isChecked
            ? window.Route.Coverage.ActiveByDefault
            : window.Route.Coverage.InactiveByDefault,
          merchantId: data.merchant_id,
          theming: {
            mode: window.Route.Theme.Mode.LightMode,
            alignment: window.Route.Theme.Alignment.Left,
          },
        })

        window.Route.Protection.on('status_change', handleInsuranceToggle)
        window.Route.Protection.on('get_quote', function () {
          wrapperRef.current?.classList.remove('hidden')
        })

        // fix: 切换页面后 route 不显示问题
        window?.Route?.Protection?.refresh()
        setTimeout(() => {
          const status = isCheckedRef.current
            ? window?.Route?.Coverage?.ActiveByDefault
            : window?.Route?.Coverage?.InactiveByDefault

          if (window?.Route?.Protection?.status === 0 && status === 1) {
            window?.Route?.Protection?.toggle()
            return
          }
          if (
            window?.Route?.Protection?.status !== 0 &&
            status !== window?.Route?.Protection?.status
          ) {
            window?.Route?.Protection?.toggle()
            return
          }
        }, 500)
      }
    },
    [handleInsuranceToggle, isChecked],
  )

  useUpdateEffect(() => {
    if (enabled && cartId && loaded) {
      getRouteConfigQuery({
        cartId,
      })
        .unwrap()
        .then((data) => {
          if (data.routeConfig) {
            renderRoute(data.routeConfig)
          }
        })
    }
  }, [loaded])

  useUpdateEffect(() => {
    if (enabled) {
      getRouteConfigQuery({
        cartId,
      })
        .unwrap()
        .then((data) => {
          if (data.routeConfig) {
            renderRoute(data.routeConfig)
          }
        })
    }
  }, [cartTotalCount, enabled])

  useEffect(() => {
    if (pathname !== '/checkout/cart') {
      window?.Route?.Protection?.unmountWidget()
    }
  }, [pathname])

  /**
   * 由于插件需要内部调用函数，无法实时获取到useState中的内容，兼容方式
   */
  useUpdateEffect(() => {
    refreshCart()
  }, [toggleRefreshCartState])

  if (!cartId) {
    return null
  }

  return (
    <div className="hidden" ref={wrapperRef}>
      <div className="route-div"></div>
      <Script
        id="route-script"
        strategy="lazyOnload"
        src="https://protection-widget.route.com/route-protection-widget.js"
        onReady={() => {
          setLoaded(true)
        }}
      />
    </div>
  )
}

export default RouteWidget
